import { useEffect, useRef, useState, useMemo, Suspense } from 'react';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { AdminNotificationsProvider } from '@/contexts/AdminNotificationsContext';

import { DemoModeBanner } from '@/components/DemoModeBanner';
import { EmployeeTestBanner } from '@/components/EmployeeTestBanner';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { NavLink } from '@/components/NavLink';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import { LogOut, ChevronDown, Settings, Search, X } from 'lucide-react';
import { GlobalExportButton } from '@/components/admin/GlobalExportButton';
import { AdminChatFloating } from '@/components/admin/AdminChatFloating';
import { ZohanAssistant } from '@/components/admin/ZohanAssistant';
import { AdminNotificationBell } from '@/components/admin/AdminNotificationBell';
import { useAdminSessionTimeout } from '@/hooks/useAdminSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import {
  adminNavSections,
  adminStandaloneItems,
  navDividerAfter,
  navGroupLabels,
  type NavSection,
  type NavItem,
} from '@/config/adminNav';
import { usePermissions } from '@/hooks/usePermissions';
import { useComplianceQueueCounts } from '@/hooks/useComplianceQueueCounts';
import PermissionDenied from '@/components/admin/PermissionDenied';
import { GUARDED_ROUTE_PREFIXES } from '@/config/routePermissions';
import paynovusLogo from '@/assets/paynovus-logo-horizontal.svg';
import { cn } from '@/lib/utils';
import { formatLabel } from '@/lib/format-labels';

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const queueCounts = useComplianceQueueCounts();
  const navBadges: Record<string, number> = {
    '/admin/compliance/held-intents': queueCounts.heldIntents,
    '/admin/compliance/cases': queueCounts.openCases,
  };
  const [query, setQuery] = useState('');

  const visibleSections = useMemo(
    () => adminNavSections.filter(s => !s.sectionPermission || hasPermission(s.sectionPermission)),
    [hasPermission],
  );

  // Helper: get all items from a section (flat items + items from sub-groups)
  const allSectionItems = (s: NavSection) => {
    const sub = (s.subGroups || []).flatMap(g => g.items);
    return [...(s.items || []), ...sub];
  };

  // Filter items across all sections by query
  const filteredSections = useMemo<NavSection[]>(() => {
    if (!query.trim()) return visibleSections;
    const q = query.toLowerCase();
    return visibleSections
      .map(s => {
        const items = (s.items || []).filter(i => i.title.toLowerCase().includes(q));
        const subGroups = (s.subGroups || [])
          .map(g => ({ ...g, items: g.items.filter(i => i.title.toLowerCase().includes(q)) }))
          .filter(g => g.items.length > 0);
        return { ...s, items, subGroups };
      })
      .filter(s => s.items.length > 0 || (s.subGroups && s.subGroups.length > 0));
  }, [query, visibleSections]);

  // Auto-open section containing active route; or all sections when searching
  const getInitialOpen = () => {
    const open: Record<string, boolean> = {};
    for (const section of visibleSections) {
      const isActive = allSectionItems(section).some(item =>
        item.end ? location.pathname === item.url : location.pathname.startsWith(item.url.split('?')[0]),
      );
      if (isActive) open[section.title] = true;
    }
    if (Object.keys(open).length === 0) open['Dashboard & Applications'] = true;
    return open;
  };

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(getInitialOpen);

  // When searching, expand all matched sections automatically
  useEffect(() => {
    if (query.trim()) {
      const open: Record<string, boolean> = {};
      filteredSections.forEach(s => { open[s.title] = true; });
      setOpenSections(open);
    }
  }, [query, filteredSections]);

  const toggleSection = (title: string) =>
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));

  return (
    <Sidebar
      className={cn('border-r border-border/60 bg-card flex flex-col', collapsed ? 'w-14' : 'w-[260px]')}
      collapsible="icon"
    >
      {/* Logo */}
      <div className="h-14 flex items-center px-3 border-b border-border/60 shrink-0">
        <Link to="/admin" className="flex items-center gap-2 min-w-0">
          <img
            src={paynovusLogo}
            alt="PayNovus"
            className={cn('shrink-0', collapsed ? 'h-5 w-auto' : 'h-7 w-auto')}
          />
          {!collapsed && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1 whitespace-nowrap">
              Admin
            </span>
          )}
        </Link>
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="px-3 py-2 border-b border-border/40 shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search navigation…"
              className="h-7 pl-8 pr-7 text-xs bg-muted/40 border-0 focus-visible:ring-1 focus-visible:ring-primary/40"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scrollable nav */}
      <SidebarContent className="flex-1 overflow-y-auto py-2 px-1.5 space-y-0.5">
        {filteredSections.map((section, idx) => {
          const isOpen = !!openSections[section.title];
          const groupLabel = !query.trim() ? navGroupLabels[section.title] : undefined;
          const hasDivider = !query.trim() && navDividerAfter.has(section.title);
          const sectionItems = allSectionItems(section);
          const activeItem = sectionItems.some(item =>
            item.end ? location.pathname === item.url : location.pathname.startsWith(item.url.split('?')[0]),
          );
          const renderItem = (item: NavItem) => (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton asChild className="h-7">
                <NavLink
                  to={item.url}
                  end={item.end}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1 rounded-md text-xs text-muted-foreground transition-all',
                    'hover:bg-muted/60 hover:text-foreground',
                  )}
                  activeClassName="bg-primary/10 text-primary font-medium"
                >
                  <item.icon className="h-3.5 w-3.5 shrink-0" />
                  {!collapsed && <span className="whitespace-normal leading-tight">{item.title}</span>}
                  {!collapsed && navBadges[item.url] > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-400 text-[10px] font-semibold">
                      {navBadges[item.url] > 99 ? '99+' : navBadges[item.url]}
                    </span>
                  )}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );

          return (
            <div key={section.title}>
              {/* Group label */}
              {!collapsed && groupLabel && (
                <div className="px-2 pt-3 pb-1">
                  <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground/60">
                    {groupLabel}
                  </span>
                </div>
              )}

              <Collapsible open={isOpen} onOpenChange={() => toggleSection(section.title)}>
                {/* Section header */}
                <CollapsibleTrigger asChild>
                  <button
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold transition-all group',
                      'hover:bg-muted/60',
                      activeItem && !isOpen
                        ? 'text-foreground bg-muted/40'
                        : 'text-foreground/70',
                    )}
                    title={collapsed ? section.title : undefined}
                  >
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: section.color }} />
                    <section.icon className="h-3.5 w-3.5 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left whitespace-normal leading-tight">{section.title}</span>
                        <ChevronDown
                          className={cn(
                            'h-3 w-3 shrink-0 text-muted-foreground/50 transition-transform duration-200',
                            isOpen && 'rotate-180',
                          )}
                        />
                      </>
                    )}
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarMenu className={cn(collapsed ? 'mt-0.5' : 'ml-4 mt-0.5 border-l border-border/50 pl-2')}>
                    {(section.items || []).map(renderItem)}
                    {(section.subGroups || []).map(group => {
                      const groupKey = `${section.title}::${group.title}`;
                      const gOpen = openSections[groupKey] !== false; // default open
                      const gActive = group.items.some(item =>
                        item.end ? location.pathname === item.url : location.pathname.startsWith(item.url.split('?')[0]),
                      );
                      return (
                        <Collapsible
                          key={group.title}
                          open={gOpen}
                          onOpenChange={() => toggleSection(groupKey)}
                        >
                          <CollapsibleTrigger asChild>
                            <button
                              className={cn(
                                'w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-all',
                                'hover:bg-muted/40',
                                gActive ? 'text-foreground/80' : 'text-muted-foreground/70',
                              )}
                            >
                              {!collapsed && (
                                <>
                                  <span className="flex-1 text-left">{group.title}</span>
                                  <ChevronDown
                                    className={cn(
                                      'h-2.5 w-2.5 shrink-0 transition-transform duration-200',
                                      gOpen && 'rotate-180',
                                    )}
                                  />
                                </>
                              )}
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className={cn(collapsed ? '' : 'ml-1')}>
                              {group.items.map(renderItem)}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </SidebarMenu>
                </CollapsibleContent>
              </Collapsible>

              {/* Divider after end of logical group */}
              {hasDivider && !query.trim() && (
                <div className="my-2 mx-2 border-t border-border/40" />
              )}
            </div>
          );
        })}
      </SidebarContent>

      {/* Footer — pinned utility items */}
      <div className={cn('border-t border-border/60 py-2 px-1.5 shrink-0 space-y-0.5')}>
        {!collapsed && (
          <p className="px-2 text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50 mb-1">
            My Workspace
          </p>
        )}
        {adminStandaloneItems.map(item => (
          <SidebarMenuItem key={item.url} className="list-none">
            <SidebarMenuButton asChild className="h-7">
              <NavLink
                to={item.url}
                className={cn(
                  'flex items-center gap-2 px-2 py-1 rounded-md text-xs text-muted-foreground transition-all',
                  'hover:bg-muted/60 hover:text-foreground',
                )}
                activeClassName="bg-primary/10 text-primary font-medium"
              >
                <item.icon className="h-3.5 w-3.5 shrink-0" />
                {!collapsed && <span className="truncate">{item.title}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </div>
    </Sidebar>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────

// sessionStorage key: MFA verified flag persists for the browser tab session.
// Cleared explicitly on sign-out so the next login always re-checks.
const MFA_SESSION_KEY = 'admin_mfa_verified';

export default function AdminLayout() {
  const { user, adminUser, adminUserLoaded, loading, signOut } = useAdminAuth();
  const { showCountdown, countdownSeconds, resetTimer } = useAdminSessionTimeout();
  const { hasPermission: checkRoutePerm } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirectedToLogin = useRef(false);
  const mainScrollRef = useRef<HTMLElement | null>(null);
  const touchPointRef = useRef<{ x: number; y: number } | null>(null);

  const canNestedElementScroll = (
    target: EventTarget | null,
    boundary: HTMLElement,
    deltaY: number,
    deltaX: number,
  ) => {
    let node = target instanceof HTMLElement ? target : null;
    while (node && node !== boundary) {
      const style = window.getComputedStyle(node);
      const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY)
        && node.scrollHeight > node.clientHeight
        && ((deltaY < 0 && node.scrollTop > 0)
          || (deltaY > 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1));
      const canScrollX = /(auto|scroll|overlay)/.test(style.overflowX)
        && node.scrollWidth > node.clientWidth
        && ((deltaX < 0 && node.scrollLeft > 0)
          || (deltaX > 0 && node.scrollLeft + node.clientWidth < node.scrollWidth - 1));
      if (canScrollY || canScrollX) return true;
      node = node.parentElement;
    }
    return false;
  };

  const scrollMainBy = (deltaY: number, deltaX = 0) => {
    const main = mainScrollRef.current;
    if (!main) return;
    main.scrollTop += deltaY;
    main.scrollLeft += deltaX;
  };

  // Read persisted MFA result — avoids re-checking (and blanking) on navigation
  const [mfaChecked, setMfaChecked] = useState(() => sessionStorage.getItem(MFA_SESSION_KEY) === '1');
  const [mfaValid, setMfaValid] = useState(() => sessionStorage.getItem(MFA_SESSION_KEY) === '1');
  const mfaCheckRunning = useRef(false);

  // MFA check — only runs once per login; sessionStorage prevents re-runs on navigation
  useEffect(() => {
    if (sessionStorage.getItem(MFA_SESSION_KEY) === '1') return;
    if (mfaCheckRunning.current) return;
    if (!user || loading || !adminUserLoaded || !adminUser) return;

    const markVerified = () => {
      sessionStorage.setItem(MFA_SESSION_KEY, '1');
      setMfaValid(true);
      setMfaChecked(true);
    };

    if (user.email === 'ai-agent@paynovus.com') {
      markVerified();
      window.history.replaceState({}, '');
      return;
    }
    if (location.state?.mfaJustVerified) {
      markVerified();
      window.history.replaceState({}, '');
      return;
    }

    let cancelled = false;
    mfaCheckRunning.current = true;
    const checkMFA = async () => {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        const hasTOTP = data?.totp?.some(f => f.status === 'verified');
        if (!hasTOTP) { navigate('/admin/mfa-setup', { replace: true }); return; }
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
          navigate('/admin/mfa-challenge', { replace: true }); return;
        }
        if (!cancelled) markVerified();
      } catch {
        if (!cancelled) markVerified();
      } finally {
        mfaCheckRunning.current = false;
      }
    };
    checkMFA();
    return () => { cancelled = true; };
  }, [user, loading, adminUserLoaded, adminUser, navigate, location.state]);

  useEffect(() => {
    if (!loading && !user && !hasRedirectedToLogin.current) {
      const timer = setTimeout(() => {
        if (hasRedirectedToLogin.current) return;
        hasRedirectedToLogin.current = true;
        navigate('/admin/login', { state: { from: location.pathname }, replace: true });
      }, 4000);
      return () => clearTimeout(timer);
    }
    if (user) hasRedirectedToLogin.current = false;
  }, [loading, user, navigate, location.pathname]);

  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  const nonAdminTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!loading && user && adminUserLoaded && adminUser === null) {
      nonAdminTimerRef.current = setTimeout(async () => {
        signOut().then(() => navigate('/admin/login'));
      }, 8000);
      return () => { if (nonAdminTimerRef.current) clearTimeout(nonAdminTimerRef.current); };
    }
    if (adminUser) {
      if (nonAdminTimerRef.current) { clearTimeout(nonAdminTimerRef.current); nonAdminTimerRef.current = null; }
    }
  }, [loading, user, adminUserLoaded, adminUser, navigate]);

  if (loading || (user && !adminUserLoaded) || (user && adminUser && !mfaChecked)) {
    return <div className="min-h-screen flex items-center justify-center"><BrandedSpinner size="lg" /></div>;
  }
  if (!user || !adminUser) {
    return <div className="min-h-screen flex items-center justify-center"><BrandedSpinner size="lg" /></div>;
  }

  const initials = adminUser.fullName
    ?.split(' ').map(n => n[0]).join('').toUpperCase() || adminUser.email[0].toUpperCase();

  const requiredRoutePermission = GUARDED_ROUTE_PREFIXES
    .find(([prefix]) => location.pathname.startsWith(prefix))?.[1] ?? null;
  const isRouteAllowed = !requiredRoutePermission || checkRoutePerm(requiredRoutePermission);

  return (
    <AdminNotificationsProvider>
    <SidebarProvider className="flex h-[100dvh] max-h-[100dvh] w-full overflow-hidden">
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="Admin"
        />
      )}
      <AdminSidebar />
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <div className="shrink-0">
          <EmployeeTestBanner />
          <DemoModeBanner />
        </div>
        <header className="h-14 border-b border-border/60 flex items-center justify-between px-4 bg-card/80 backdrop-blur-xl shrink-0">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
            <div className="hidden md:flex items-center gap-2">
              <span className="font-display text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Admin Console</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GlobalExportButton />
            <AdminNotificationBell />
            <Link to="/admin/settings">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                <Settings className="h-4 w-4" />
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative flex items-center gap-2 px-2 h-9 rounded-lg hover:bg-muted/50"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:flex flex-col items-start leading-none">
                    <span className="text-xs font-semibold">{adminUser.fullName || 'Admin User'}</span>
                    <span className="text-[10px] text-muted-foreground">{adminUser.email}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{adminUser.fullName || 'Admin User'}</span>
                    <span className="text-xs text-muted-foreground font-normal">{adminUser.email}</span>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {adminUser.roles.map(role => (
                        <span
                          key={role}
                          className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded capitalize"
                        >
                          {formatLabel(role)}
                        </span>
                      ))}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/admin/settings">
                    <Settings className="mr-2 h-4 w-4" />Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut className="mr-2 h-4 w-4" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main
          ref={mainScrollRef}
          className="flex-1 min-h-0 min-w-0 p-6 bg-gradient-mesh overflow-auto overscroll-contain [scrollbar-gutter:stable]"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
          onWheelCapture={(event) => {
            const main = mainScrollRef.current;
            if (!main || canNestedElementScroll(event.target, main, event.deltaY, event.deltaX)) return;
            event.preventDefault();
            scrollMainBy(event.deltaY, event.deltaX);
          }}
          onTouchStartCapture={(event) => {
            const touch = event.touches[0];
            touchPointRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
          }}
          onTouchMoveCapture={(event) => {
            const main = mainScrollRef.current;
            const previous = touchPointRef.current;
            const touch = event.touches[0];
            if (!main || !previous || !touch) return;

            const deltaX = previous.x - touch.clientX;
            const deltaY = previous.y - touch.clientY;
            touchPointRef.current = { x: touch.clientX, y: touch.clientY };

            if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;
            if (canNestedElementScroll(event.target, main, deltaY, deltaX)) return;

            event.preventDefault();
            scrollMainBy(deltaY, deltaX);
          }}
          onTouchEndCapture={() => { touchPointRef.current = null; }}
          onTouchCancelCapture={() => { touchPointRef.current = null; }}
        >
          {/* Nested Suspense: only the page content suspends on lazy-load,
              NOT the whole layout — prevents the full-screen spinner on navigation */}
          <Suspense fallback={<div className="flex items-center justify-center h-64"><BrandedSpinner size="md" /></div>}>
            {isRouteAllowed
              ? <Outlet />
              : <PermissionDenied route={location.pathname} requiredPermission={requiredRoutePermission} />}
          </Suspense>
        </main>
      </div>
      <AdminChatFloating />
      <ZohanAssistant />
    </SidebarProvider>
    </AdminNotificationsProvider>
  );
}
